import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import ToolFooter from '../../components/ui/ToolFooter';
import AuthorCredit from '../../components/ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../../components/ui/FreshnessDate';

export default function MarginCalculator() {
  const [clients, setClients] = useState(50);
  const [hours, setHours] = useState(2);
  const [rate, setRate] = useState(150);

  const lostCapacity = clients * hours * rate;
  
  // Zap recovery: reduces hours to 0.2
  const zapHours = 0.2;
  const newCapacityCost = clients * zapHours * rate;
  const zapRecovery = lostCapacity - newCapacityCost;
  const timeSaved = clients * (hours - zapHours);

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to recover margin on tech-averse accounting clients",
    "dateModified": getFreshnessDateIso(),
    "step": [
      {"@type": "HowToStep", "text": "Identify clients who submit receipts in shoeboxes or fail to adopt mobile apps."},
      {"@type": "HowToStep", "text": "Assign them a dedicated Zap Expenses WhatsApp number instead of a login portal."},
      {"@type": "HowToStep", "text": "Review their AI-parsed receipts automatically structured for quarterly MTD submission."}
    ]
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Accountant Margin Calculator for MTD 2026",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": "Calculate the impact of Making Tax Digital (MTD) on your accounting firm's margins. Identify unbillable hours and recover billable revenue on non-digital clients.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "GBP"
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-emerald-500/30">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify([howToSchema, softwareSchema])}</script>
        <title>Accountant Margin Calculator | Zap Expenses</title>
        <meta name="description" content="Calculate the impact of MTD 2026 on your firm's margins. Identify unbillable hours, automate data entry for non-digital clients, and recover billable revenue." />
        <meta name="keywords" content="accountant margin calculator, MTD profitability, unbillable hours tax, accounting automation tools, MTD ITSA partners" />
      </Helmet>

      <div className="max-w-4xl mx-auto py-16 px-6">
        <FreshnessDate className="mb-4" />
        
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
          The Accountant <span className="text-emerald-400">Margin Calculator</span>
        </h1>
        
        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
          <p className="text-xl font-medium leading-relaxed mb-4">
            <strong>"UK accountancy firms lose an average of £48,000 in billable revenue per full-time accountant every year due to manual inefficiencies."</strong>
          </p>
          <div className="text-[10px] text-indigo-300 italic border-t border-indigo-500/20 pt-2">
            Source: <a href="https://staxxsolutions.co.uk/the-7-automations-winning-firms-use-that-most-practices-havent-heard-of/" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-400 text-indigo-300">Staxx Solutions, 7 Automations Winning Firms Use</a>
          </div>
        </div>
        
        <div className="prose prose-invert max-w-none text-slate-300 mb-12">
          <p>
            When <strong>Making Tax Digital</strong> hits, non-digital clients will require four times the administrative effort if you try to manage their quarterly submissions manually. Calculate the financial impact to your firm below by mapping out unbillable chasing time against your charge-out rate.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Inputs */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold mb-6">Your Firm's Metrics</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1 mb-2">
                  Total 'Paper' or Tech-Averse Clients
                </label>
                <input
                  type="number"
                  value={clients}
                  onChange={(e) => setClients(Number(e.target.value))}
                  className="w-full bg-black/40 border border-white/10 rounded-xl py-4 px-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-mono text-lg"
                />
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1 mb-2">
                  Hours / Month / Client (Chasing & Entry)
                </label>
                <input
                  type="number"
                  value={hours}
                  step="0.5"
                  onChange={(e) => setHours(Number(e.target.value))}
                  className="w-full bg-black/40 border border-white/10 rounded-xl py-4 px-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-mono text-lg"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1 mb-2">
                  Firm's Hourly Charge-out Rate
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">£</span>
                  <input
                    type="number"
                    value={rate}
                    onChange={(e) => setRate(Number(e.target.value))}
                    className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-8 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-mono text-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="flex flex-col gap-6">
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 shadow-2xl">
              <p className="text-red-400 text-sm font-bold uppercase tracking-widest mb-2">Monthly Lost Capacity</p>
              <p className="text-4xl font-extrabold text-white font-mono leading-none">
                £{lostCapacity.toLocaleString()}
              </p>
              <p className="text-sm text-slate-400 mt-3">
                Value of time currently spent on non-billable chasing and data entry.
              </p>
            </div>

            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 shadow-2xl flex-1 flex flex-col justify-center">
              <p className="text-emerald-400 text-sm font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
                <span>⚡</span> Zap Recovery
              </p>
              <p className="text-5xl font-extrabold text-emerald-400 font-mono leading-none mb-3">
                £{Math.round(zapRecovery).toLocaleString()}
              </p>
              <p className="text-lg text-white font-semibold mb-1">
                {Math.round(timeSaved)} hours saved per month
              </p>
              <p className="text-sm text-slate-400">
                By automating capture via WhatsApp and cutting time to exactly 12 minutes (0.2 hours) per client.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center bg-slate-900 border border-white/5 rounded-2xl p-10 mt-8">
          <h2 className="text-3xl font-bold mb-4">Stop wasting chargeable time.</h2>
          <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
            Give your most difficult clients a WhatsApp number, not another login to forget. Let Zap Expenses handle the ingestion, line-item extraction, and categorization.
          </p>
          <a href="mailto:partners@zapexpenses.com" className="inline-block px-10 py-5 bg-white text-black font-bold rounded-xl shadow-lg hover:bg-slate-200 transition-all uppercase tracking-wide text-lg">
            Book a Partner Demo
          </a>
        </div>

        <div id="stat-leakage-2026" className="mt-10 mb-2 bg-black/40 border border-slate-800 p-6 rounded-xl shadow-inner relative overflow-hidden text-center md:text-left">
          <p className="text-emerald-400 font-bold uppercase tracking-widest text-xs mb-1">The "Reasonable Care" Loss</p>
          <p className="text-2xl font-extrabold text-white mb-2">31% of the HMRC Tax Gap</p>
          <p className="text-slate-400 text-sm">
            "HMRC attributes 31% of the UK's £46.8bn tax gap to 'Failure to Take Reasonable Care'—the largest single cause of lost revenue."
          </p>
          <p className="mt-3 text-[10px] text-slate-500 italic border-t border-slate-800/50 pt-2 text-center md:text-left">
            Source: <a href="https://www.gov.uk/government/statistics/measuring-tax-gaps/7-tax-gaps-illustrative-tax-gap-by-behaviour" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">HMRC, Tax gaps: illustrative tax gap by behaviour</a>
          </p>
        </div>

        <div className="mt-6 mb-2 text-center">
          <a href="https://www.gov.uk/guidance/using-making-tax-digital-for-income-tax" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-indigo-400 hover:text-indigo-300 underline">
            Review Practitioner rules for MTD ITSA on Gov.uk →
          </a>
        </div>


      </div>
    </div>
  );
}
